import { useSearch } from "@tanstack/react-location";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAssetsWithBalance } from "../../hooks/useAssetsWithBalance";
import { TLocationGenerics } from "../../pages/routes";
import { transformGQLAdaAsset } from "../../utils/assets.utils";
import { getStats2Sdk } from "../client/statsSdk";
import { Asset } from "../generated/stats2.sdk";

export const useAssetMetadata = (additionalIds?: string[]) => {
  const { given, taken } = useSearch<TLocationGenerics>();
  const { assetsWithBalance } = useAssetsWithBalance();

  const ids = useMemo(() => {
    const map = new Map();
    const fullList = [
      ...[given, taken].filter(Boolean),
      ...(assetsWithBalance?.map(({ metadata: { assetId } }) => assetId) ?? []),
      ...(additionalIds ?? []),
    ];

    fullList.forEach((id) => id && map.set(id, id));
    return [...map.values()].sort();
  }, [given, taken, assetsWithBalance, additionalIds]);

  return useQuery(
    ["assets", ids],
    async () => {
      const { getAssetsByIds } = await getStats2Sdk();

      const data = await getAssetsByIds({ ids });
      return data.assets.map((asset: Asset) => transformGQLAdaAsset(asset));
    },
    {
      enabled: Boolean(ids?.length && ids.filter(Boolean)),
      staleTime: 1000 * (additionalIds ? 5 : 10 * 60), // 10 minutes if no additionalIds are passed in.
      cacheTime: 1000 * 60 * 25,
      refetchOnWindowFocus: false,
    }
  );
};
